import React, { useState, useEffect, useRef } from "react";
import { httpClient } from "../../HttpClient";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";
import { useEtatGlobal } from "../EtatGlobal";
import { IoIosSearch } from "react-icons/io";
import {
  Eye,
  X,
  FileText,
  User,
  Building2,
  CreditCard,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Trash2,
  Download,
  RotateCcw,
  Upload,
} from "lucide-react";

const API_BASE = "https://backend-myalfa.vercel.app/api";

const STATUS_META = {
  a_revoir: {
    label: "À revoir",
    cls: "bg-amber-100 text-amber-700 border-amber-200",
    Icon: RotateCcw,
  },
  completed: {
    label: "Signé",
    cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
    Icon: CheckCircle2,
  },
  sent: {
    label: "Envoyé",
    cls: "bg-blue-100 text-blue-700 border-blue-200",
    Icon: Clock,
  },
  declined: {
    label: "Refusé",
    cls: "bg-red-100 text-red-700 border-red-200",
    Icon: AlertCircle,
  },
  annulé: {
    label: "Annulé",
    cls: "bg-red-100 text-red-700 border-red-200",
    Icon: AlertCircle,
  },
};

function statusBadge(status) {
  const s = STATUS_META[status] || {
    label: status || "—",
    cls: "bg-gray-100 text-gray-600 border-gray-200",
    Icon: FileText,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${s.cls}`}
    >
      <s.Icon className="w-2.5 h-2.5" />
      {s.label}
    </span>
  );
}

function formatIban(rib) {
  if (!rib) return "";
  return rib
    .replace(/[^a-zA-Z0-9]/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Certains RUM historiques portent un suffixe technique "-DUP2", "-DUP3"...
// (renumérotation d'anciens doublons en base, cf. reserveNextRum côté
// backend) — jamais affiché côté utilisateur, uniquement en base.
function displayRum(rum) {
  if (!rum) return rum;
  return rum.replace(/-DUP\d+$/, "");
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right max-w-[55%] break-words">
        {value}
      </span>
    </div>
  );
}

const isMandatSigne = (m) =>
  m?.status === "completed" || m?.signerStatus === "completed";

// Un document mandat est téléchargeable s'il existe un PDF importé manuellement
// ou une enveloppe DocuSign (signée ou non : DocuSign renvoie le PDF dans son
// état courant via documents/combined).
const hasMandatDocument = (m) =>
  Boolean(m?.docusignEnvelopeId || m?.signedDocumentUrl);

function getMandatInfoValue(mandat) {
  if (mandat.status === "annulé") return "annule";
  if (mandat.status === "a_revoir" || mandat.signerStatus === "a_revoir")
    return "a_revoir";
  if (mandat.signerStatus === "completed" || mandat.status === "completed") {
    return mandat.docusignEnvelopeId ? "docusign" : "cloture";
  }
  return "aucune";
}

const INFO_FILTER_OPTIONS = [
  { value: "annule", label: "Annulé" },
  { value: "a_revoir", label: "À revoir" },
  { value: "docusign", label: "DocuSign" },
  { value: "cloture", label: "Clôturé" },
  { value: "aucune", label: "—" },
];

const OUI_NON_FILTER_OPTIONS = [
  { value: "Oui", label: "Oui" },
  { value: "Non", label: "Non" },
];

const FILTERABLE_COLUMNS = {
  statut: {
    label: "Statut",
    getValue: (mandat) => mandat.signerStatus || mandat.status || null,
    getOptions: (data) => {
      const values = [
        ...new Set(
          data.map((m) => m.signerStatus || m.status).filter(Boolean)
        ),
      ];
      return values.map((value) => ({
        value,
        label: STATUS_META[value]?.label || value,
      }));
    },
  },
  info: {
    label: "Info",
    getValue: getMandatInfoValue,
    getOptions: () => INFO_FILTER_OPTIONS,
  },
  cabinet: {
    label: "Cabinet",
    getValue: (mandat) => mandat.cabinet || "—",
    getOptions: (data) => {
      const values = [...new Set(data.map((m) => m.cabinet || "—"))];
      return values.map((value) => ({ value, label: value }));
    },
  },
  documentRib: {
    label: "Document RIB",
    getValue: (mandat) => (mandat.document ? "Oui" : "Non"),
    getOptions: () => OUI_NON_FILTER_OPTIONS,
  },
  mandatSigne: {
    label: "Mandat signé",
    getValue: (mandat) =>
      (mandat.docusignEnvelopeId || mandat.signedDocumentUrl) &&
      (mandat.status === "completed" || mandat.signerStatus === "completed")
        ? "Oui"
        : "Non",
    getOptions: () => OUI_NON_FILTER_OPTIONS,
  },
};

const PAGE_SIZE = 20;

export default function Mandat() {
  const { mandats, setMandats, loadingMandats, utilisateur } = useEtatGlobal();
  const isAdmin = utilisateur?.fonctionalite?.includes("Admin") || false;
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [cloturant, setCloturant] = useState(new Set());
  const [annulant, setAnnulant] = useState(new Set());
  const [supprimant, setSupprimant] = useState(new Set());
  const [downloadingDoc, setDownloadingDoc] = useState(new Set());
  const [showAnnulModal, setShowAnnulModal] = useState(null);
  const [motifInput, setMotifInput] = useState("");
  const [revoyant, setRevoyant] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [signedPdfFile, setSignedPdfFile] = useState(null);
  const [filterRows, setFilterRows] = useState([]);
  const [activeColumnFilter, setActiveColumnFilter] = useState(null);
  const [columnFilterPosition, setColumnFilterPosition] = useState(null);
  const detailRef = useRef(null);
  const signedPdfInputRef = useRef(null);
  const filterRef = useRef(null);

  // Télécharge le document mandat, signé ou non : le backend renvoie le PDF
  // importé manuellement s'il existe, sinon le PDF DocuSign dans son état courant.
  const handleTelechargerMandat = (mandat, e) => {
    if (e) e.stopPropagation();
    const signe = isMandatSigne(mandat);
    setDownloadingDoc((prev) => new Set([...prev, mandat.id]));
    httpClient
      .get(`${API_BASE}/mandat/${mandat.id}/signed-document`, {
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const formattedDate = new Date()
          .toLocaleDateString("fr-FR")
          .replace(/\//g, "-");
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Mandat-${signe ? "Signe" : "Non-Signe"}-${
            displayRum(mandat.rum) || mandat.id
          } - ${formattedDate}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Erreur téléchargement mandat :", err);
        alert("Impossible de télécharger le document mandat.");
      })
      .finally(() => {
        setDownloadingDoc((prev) => {
          const next = new Set(prev);
          next.delete(mandat.id);
          return next;
        });
      });
  };

  const handleSupprimer = (mandat) => {
    setSupprimant((prev) => new Set([...prev, mandat.id]));
    setMandats((prev) => prev.filter((m) => m.id !== mandat.id));
    if (selected?.id === mandat.id) setSelected(null);
    httpClient
      .delete(`${API_BASE}/mandat/${mandat.id}`)
      .catch((err) => {
        console.error("Erreur suppression mandat :", err);
        setMandats((prev) => [...prev, mandat]);
      })
      .finally(() => {
        setSupprimant((prev) => {
          const next = new Set(prev);
          next.delete(mandat.id);
          return next;
        });
      });
  };

  const handleCloture = async (mandat, signedFile) => {
    setCloturant((prev) => new Set([...prev, mandat.id]));
    setMandats((prev) =>
      prev.map((m) =>
        m.id === mandat.id
          ? { ...m, status: "completed", signerStatus: "completed" }
          : m
      )
    );

    let payload;
    if (signedFile) {
      // Upload direct navigateur → Blob (contourne la limite 4,5 Mo).
      const signedDocumentUrl = await uploadFileToBlob(
        signedFile,
        "https://backend-myalfa.vercel.app/api/mandat/upload-token"
      ).catch((err) => {
        console.error("Erreur upload PDF signé :", err);
        return null;
      });
      if (signedDocumentUrl) payload = { signedDocumentUrl };
    }

    httpClient
      .put(`${API_BASE}/mandat/${mandat.id}/complete`, payload)
      .then((res) => {
        // Pour une ligne « lien envoyé », le serveur vient de créer le vrai
        // mandat : la ligne prend son id et n'est plus un simple lien.
        if (res.data) {
          setMandats((prev) =>
            prev.map((m) =>
              m.id === mandat.id
                ? { ...m, ...res.data, lienEnvoye: false }
                : m
            )
          );
        }
      })
      .catch((err) => {
        console.error("Erreur clôture mandat :", err);
        setMandats((prev) =>
          prev.map((m) =>
            m.id === mandat.id
              ? {
                  ...m,
                  status: mandat.status,
                  signerStatus: mandat.signerStatus,
                }
              : m
          )
        );
      })
      .finally(() => {
        setCloturant((prev) => {
          const next = new Set(prev);
          next.delete(mandat.id);
          return next;
        });
      });
  };

  const handleRevoir = (mandat) => {
    setRevoyant((prev) => new Set([...prev, mandat.id]));
    setMandats((prev) =>
      prev.map((m) =>
        m.id === mandat.id
          ? { ...m, status: "a_revoir", signerStatus: "a_revoir" }
          : m
      )
    );
    httpClient
      .put(`${API_BASE}/mandat/${mandat.id}/revoir`)
      .catch((err) => {
        console.error("Erreur revoir mandat :", err);
        setMandats((prev) =>
          prev.map((m) =>
            m.id === mandat.id
              ? {
                  ...m,
                  status: mandat.status,
                  signerStatus: mandat.signerStatus,
                }
              : m
          )
        );
      })
      .finally(() => {
        setRevoyant((prev) => {
          const next = new Set(prev);
          next.delete(mandat.id);
          return next;
        });
      });
  };

  const confirmAnnulation = () => {
    const motif = motifInput.trim();
    if (!motif) return;
    const mandat = showAnnulModal;
    setShowAnnulModal(null);
    setMotifInput("");
    setAnnulant((prev) => new Set([...prev, mandat.id]));
    setMandats((prev) =>
      prev.map((m) =>
        m.id === mandat.id
          ? {
              ...m,
              status: "annulé",
              // Le badge et le classement de dédoublonnage lisent
              // `signerStatus || status` en priorité — sans mettre aussi
              // signerStatus à jour ici, l'affichage optimiste resterait
              // sur l'ancien statut ("Envoyé") jusqu'à la réponse serveur.
              signerStatus: "annulé",
              motifAnnulation: motif,
            }
          : m
      )
    );
    httpClient
      .put(`${API_BASE}/mandat/${mandat.id}/annuler`, { motif })
      .then((res) => {
        if (res.data) {
          setMandats((prev) =>
            prev.map((m) =>
              m.id === mandat.id
                ? { ...m, ...res.data, lienEnvoye: false }
                : m
            )
          );
        }
      })
      .catch((err) => {
        console.error("Erreur annulation mandat :", err);
        setMandats((prev) =>
          prev.map((m) =>
            m.id === mandat.id
              ? {
                  ...m,
                  status: mandat.status,
                  signerStatus: mandat.signerStatus,
                  motifAnnulation: mandat.motifAnnulation,
                }
              : m
          )
        );
      })
      .finally(() => {
        setAnnulant((prev) => {
          const next = new Set(prev);
          next.delete(mandat.id);
          return next;
        });
      });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterRows]);

  useEffect(() => {
    function handleOutside(e) {
      if (detailRef.current && !detailRef.current.contains(e.target)) {
        setSelected(null);
      }
    }
    if (selected) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [selected]);

  useEffect(() => {
    function handleFilterOutside(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setActiveColumnFilter(null);
      }
    }
    if (activeColumnFilter)
      document.addEventListener("mousedown", handleFilterOutside);
    return () =>
      document.removeEventListener("mousedown", handleFilterOutside);
  }, [activeColumnFilter]);

  const toggleColumnValue = (column, value) => {
    setFilterRows((prev) => {
      const existing = prev.find((f) => f.column === column);
      if (!existing) return [...prev, { column, selectedValues: [value] }];
      const alreadySelected = existing.selectedValues.includes(value);
      return prev.map((f) =>
        f.column === column
          ? {
              ...f,
              selectedValues: alreadySelected
                ? f.selectedValues.filter((v) => v !== value)
                : [...f.selectedValues, value],
            }
          : f
      );
    });
  };

  const handleResetColumnFilter = (column) => {
    setFilterRows((prev) =>
      prev.map((f) => (f.column === column ? { ...f, selectedValues: [] } : f))
    );
  };

  const activeFilters = filterRows
    .filter((f) => f.selectedValues?.length > 0)
    .map((f) => [f.column, f.selectedValues]);

  const activeFilterCount = activeFilters.length;

  const handleClearAllFilters = () => {
    setFilterRows([]);
  };

  // Un même client/lead peut avoir plusieurs enveloppes (renvoi DocuSign,
  // doublon...) : on garde uniquement la version la plus avancée AVANT
  // d'appliquer la recherche/les filtres. Sinon, filtrer par statut peut
  // retirer la version "Signé" et faire resurgir un doublon "Envoyé" obsolète
  // pour le même mandat, au lieu de simplement le masquer.
  //
  // Regroupement par identité réelle (clientId/leadId), pas par rum : chaque
  // mandat reçoit désormais un RUM unique dès sa création (réservation
  // atomique, voir MandatSignController.js#reserveNextRum) — deux tentatives
  // du même client n'ont donc plus forcément le même rum, contrairement à
  // avant.
  const dedupedMandats = Object.values(
    mandats.reduce((acc, mandat) => {
      const key = mandat.clientId
        ? `client-${mandat.clientId}`
        : mandat.leadId
        ? `lead-${mandat.leadId}`
        : mandat.rum || `${mandat.nom}-${mandat.rib}`;
      if (!key) return acc;
      const existing = acc[key];
      if (!existing) {
        acc[key] = mandat;
        return acc;
      }

      // Priorité 1 : présence d'un document téléchargeable (DocuSign ou
      // import manuel). Priorité 2 : statut le plus avancé. Priorité 3 : le
      // plus récent (id le plus grand).
      const rank = (m) => {
        const status = m.signerStatus || m.status;
        let score = hasMandatDocument(m) ? 10 : 0;
        if (status === "completed") score += 5;
        else if (status === "sent") score += 2;
        else if (status === "a_revoir") score += 1;
        return score;
      };

      const existingRank = rank(existing);
      const currentRank = rank(mandat);

      if (
        currentRank > existingRank ||
        (currentRank === existingRank && mandat.id > existing.id)
      ) {
        acc[key] = mandat;
      }
      return acc;
    }, {})
  );

  const getFilterValueLabel = (column, value) =>
    FILTERABLE_COLUMNS[column]
      ?.getOptions(dedupedMandats)
      .find((opt) => opt.value === value)?.label || value;

  const filtered = dedupedMandats
    .filter((m) => {
      const q = search.toLowerCase();
      if (!q) return true;
      const nom = m.nom?.toLowerCase() || "";
      const clientNom = m.client?.nom?.toLowerCase() || "";
      return nom.includes(q) || clientNom.includes(q);
    })
    .filter((m) =>
      filterRows.every((f) => {
        if (!f.selectedValues?.length) return true;
        const config = FILTERABLE_COLUMNS[f.column];
        if (!config) return true;
        return f.selectedValues.includes(config.getValue(m));
      })
    )
    .sort(
      (a, b) =>
        new Date(b.dateSignature || b.lienEnvoyeLe || 0) -
        new Date(a.dateSignature || a.lienEnvoyeLe || 0)
    );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const FilterableTableTh = ({ columnKey, label }) => (
    <th className="px-3 py-2.5 text-white/90 font-normal text-center uppercase tracking-wide">
      <div className="flex items-center justify-center gap-1">
        {label}
        <button
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            setColumnFilterPosition({ top: rect.bottom + 6, left: rect.left });
            setActiveColumnFilter((prev) =>
              prev === columnKey ? null : columnKey
            );
          }}
        >
          <ChevronDown className="w-3 h-3 opacity-80 hover:opacity-100" />
        </button>
      </div>
    </th>
  );

  return (
    <div className="relative">
      {/* En-tête */}
      <div className="mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-900 m-0">
            Mandats SEPA
          </h1>
          <div className="bg-[#9B1B5D]/80 text-white/90 text-xs px-1.5 py-1.5 rounded-full font-medium flex items-center justify-center leading-none">
            {filtered.length}
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Liste de tous les mandats de prélèvement
        </p>
      </div>

      {/* Barre de recherche */}
      <div className="flex items-center gap-4 mb-5">
        <div className="relative flex-1 max-w-xs">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher Client/Prospect..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 w-full rounded-full bg-white border border-gray-200 text-xs focus:outline-none focus:border-gray-300"
          />
        </div>
      </div>

      {activeFilterCount > 0 && (
        <div className="w-full mb-4 px-3 py-2 flex items-center gap-5 text-xs text-slate-700">
          {/* 🧮 Infos filtres */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
            <span className="font-medium px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
              {activeFilterCount} filtre{activeFilterCount > 1 ? "s" : ""}{" "}
              actif
              {activeFilterCount > 1 ? "s" : ""}
            </span>

            {activeFilters.map(([key, values]) => (
              <span key={key} className="text-slate-600">
                <strong>{FILTERABLE_COLUMNS[key]?.label || key} :</strong>{" "}
                {values.map((v) => getFilterValueLabel(key, v)).join(", ")}
              </span>
            ))}
          </div>

          {/* ❌ Tout effacer */}
          <button
            onClick={handleClearAllFilters}
            className="text-slate-500 hover:text-[#9B1B5D] text-xs transition-colors px-3 py-2 bg-slate-50 border border-slate-200 rounded-md"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* Tableau */}
      <div className="mt-4 w-full overflow-x-auto relative">
        {loadingMandats && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#f0d6e0] overflow-hidden rounded z-10">
            <div
              className="h-full bg-[#840040]"
              style={{
                width: "40%",
                animation: "progress 1.2s ease-in-out infinite",
              }}
            />
          </div>
        )}

        <table
          className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
          style={{ fontSize: "0.68rem" }}
        >
          <thead>
            <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                RUM
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Nom
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Email
              </th>
              <FilterableTableTh columnKey="statut" label="Statut" />
              <FilterableTableTh columnKey="info" label="Info" />
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Client
              </th>
              <FilterableTableTh columnKey="cabinet" label="Cabinet" />
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                IBAN
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Date de création
              </th>
              <FilterableTableTh columnKey="documentRib" label="Document RIB" />
              <FilterableTableTh columnKey="mandatSigne" label="Mandat signé" />
              <th className="px-3 py-2.5 text-white/90 font-normal text-center uppercase tracking-wide"></th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {loadingMandats ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 12 }).map((__, j) => (
                    <td key={j} className="px-3 py-2.5">
                      <div
                        className="h-3 bg-slate-100 rounded"
                        style={{ width: 60 + ((j * 15) % 60) }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : pageItems.length === 0 ? (
              <tr>
                <td
                  colSpan={12}
                  className="py-16 text-center text-gray-400 text-xs"
                >
                  Aucun mandat trouvé
                </td>
              </tr>
            ) : (
              pageItems.map((mandat) => (
                <tr
                  key={mandat.id}
                  onClick={() => setSelected(mandat)}
                  className={`cursor-pointer transition-colors hover:bg-slate-50 ${
                    selected?.id === mandat.id ? "bg-[#fdf2f6]" : ""
                  }`}
                >
                  {/* RUM */}
                  <td className="px-3 py-2.5 font-mono text-gray-700">
                    {displayRum(mandat.rum) || "—"}
                  </td>

                  {/* Nom */}
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-full bg-[#f5ede8] flex items-center justify-center text-[#840040] font-semibold flex-shrink-0 text-[9px]">
                        {(mandat.nom || "?").charAt(0).toUpperCase()}
                      </div>
                      <span className="text-gray-800 font-medium">
                        {mandat.nom || "—"}
                      </span>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-3 py-2.5 text-gray-500">
                    {mandat.email || "—"}
                  </td>

                  {/* Statut */}
                  <td className="px-3 py-2.5 text-center">
                    {statusBadge(mandat.signerStatus || mandat.status)}
                  </td>

                  {/* Info */}
                  <td className="px-3 py-2.5 text-center max-w-[120px]">
                    {mandat.status === "annulé" ? (
                      <span
                        className="text-[9px] text-red-400 font-normal tracking-wide cursor-default"
                        title={mandat.motifAnnulation || ""}
                      >
                        {mandat.motifAnnulation
                          ? mandat.motifAnnulation.length > 22
                            ? mandat.motifAnnulation.slice(0, 22) + "…"
                            : mandat.motifAnnulation
                          : "Annulé"}
                      </span>
                    ) : mandat.status === "a_revoir" ||
                      mandat.signerStatus === "a_revoir" ? (
                      <span className="text-[9px] text-amber-600 font-medium tracking-wide">
                        À revoir
                      </span>
                    ) : mandat.signerStatus === "completed" ||
                      mandat.status === "completed" ? (
                      <span className="text-[9px] text-gray-400 font-normal tracking-wide">
                        {mandat.docusignEnvelopeId ? "DocuSign" : "Clôturé"}
                      </span>
                    ) : mandat.lienEnvoye ? (
                      <span className="text-[9px] text-gray-400 font-normal tracking-wide">
                        Lien envoyé le {formatDate(mandat.lienEnvoyeLe)}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>

                  {/* Client / Prospect */}
                  <td className="px-3 py-2.5">
                    {mandat.client ? (
                      <div className="flex items-center gap-1 text-gray-700">
                        <Building2 className="w-3 h-3 text-[#840040] flex-shrink-0" />
                        {mandat.client.nom}
                      </div>
                    ) : mandat.leadId ? (
                      <div className="flex items-center gap-1 text-gray-500">
                        <User className="w-3 h-3 flex-shrink-0" />
                        <span>Lead #{mandat.leadId}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>

                  {/* Cabinet */}
                  <td className="px-3 py-2.5 text-center text-gray-600">
                    {mandat.cabinet || "—"}
                  </td>

                  {/* IBAN */}
                  <td className="px-3 py-2.5 font-mono text-gray-500">
                    {mandat.rib ? formatIban(mandat.rib) : "—"}
                  </td>

                  {/* Date signature */}
                  <td className="px-3 py-2.5 text-gray-500">
                    {formatDate(mandat.dateSignature)}
                  </td>

                  {/* Document RIB */}
                  <td className="px-3 py-2.5 text-center">
                    {mandat.document ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(mandat.document, "_blank");
                        }}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#f5ede8] text-[#840040] hover:bg-[#840040] hover:text-white transition-colors"
                        title="Voir le document RIB"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>

                  {/* Mandat signé (DocuSign ou import manuel) — icône grisée
                      mais cliquable si non signé : télécharge le PDF DocuSign
                      dans son état courant (non signé) */}
                  <td className="px-3 py-2.5 text-center">
                    {hasMandatDocument(mandat) ? (
                      <button
                        onClick={(e) => handleTelechargerMandat(mandat, e)}
                        disabled={downloadingDoc.has(mandat.id)}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          isMandatSigne(mandat)
                            ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white"
                            : "bg-gray-100 text-gray-400 hover:bg-gray-400 hover:text-white"
                        }`}
                        title={
                          isMandatSigne(mandat)
                            ? "Télécharger le mandat signé"
                            : "Télécharger le mandat non signé"
                        }
                      >
                        {downloadingDoc.has(mandat.id) ? (
                          <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-end gap-1.5">
                      {mandat.status !== "completed" &&
                        mandat.signerStatus !== "completed" &&
                        mandat.status !== "annulé" && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSignedPdfFile(null);
                                setShowConfirmModal({
                                  type: "cloture",
                                  mandat,
                                });
                              }}
                              disabled={cloturant.has(mandat.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white text-[10px] font-medium transition-colors disabled:cursor-not-allowed"
                            >
                              {cloturant.has(mandat.id) && (
                                <span className="animate-spin inline-block w-2.5 h-2.5 border border-white border-t-transparent rounded-full" />
                              )}
                              Cloturer
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowAnnulModal(mandat);
                                setMotifInput("");
                              }}
                              disabled={annulant.has(mandat.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white text-[10px] font-medium transition-colors disabled:cursor-not-allowed"
                            >
                              {annulant.has(mandat.id) && (
                                <span className="animate-spin inline-block w-2.5 h-2.5 border border-white border-t-transparent rounded-full" />
                              )}
                              Annuler
                            </button>
                          </>
                        )}
                      {/* Modifier masqué temporairement — ajustements à venir sur ce bouton */}
                      {false &&
                        (mandat.status === "completed" ||
                          mandat.signerStatus === "completed") && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowConfirmModal({ type: "revoir", mandat });
                          }}
                          disabled={revoyant.has(mandat.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-[10px] font-medium transition-colors disabled:cursor-not-allowed"
                        >
                          {revoyant.has(mandat.id) ? (
                            <span className="animate-spin inline-block w-2.5 h-2.5 border border-white border-t-transparent rounded-full" />
                          ) : (
                            <RotateCcw className="w-2.5 h-2.5" />
                          )}
                          Modifier
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowConfirmModal({ type: "supprimer", mandat });
                          }}
                          disabled={supprimant.has(mandat.id)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-1">
            <span className="text-xs text-gray-400">
              {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, filtered.length)} sur{" "}
              {filtered.length} mandats
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs transition-colors ${
                      currentPage === page
                        ? "bg-[#840040] text-white"
                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {activeColumnFilter && (
        <div
          ref={filterRef}
          style={{
            position: "fixed",
            top: columnFilterPosition.top,
            left: columnFilterPosition.left,
            zIndex: 10000,
          }}
          className="bg-white border border-slate-200 rounded-lg shadow-lg w-56 max-h-72 flex flex-col"
        >
          <div className="px-3 py-2 border-b font-semibold text-sm">
            {FILTERABLE_COLUMNS[activeColumnFilter]?.label}
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-2">
            {FILTERABLE_COLUMNS[activeColumnFilter]
              ?.getOptions(dedupedMandats)
              .map((opt) => {
                const activeFilter = filterRows.find(
                  (f) => f.column === activeColumnFilter
                );
                const checked =
                  activeFilter?.selectedValues.includes(opt.value) ?? false;

                return (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm py-1 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        toggleColumnValue(activeColumnFilter, opt.value)
                      }
                    />
                    {opt.label}
                  </label>
                );
              })}
          </div>

          <div className="flex items-center justify-between px-3 py-2 border-t">
            <button
              onClick={() => handleResetColumnFilter(activeColumnFilter)}
              className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              Réinitialiser
            </button>
            <button
              onClick={() => setActiveColumnFilter(null)}
              className="text-xs px-2 py-1 rounded-md bg-[#9B1B5D] hover:bg-[#7A1548] text-white transition-colors"
            >
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* Panneau de détail — même design que les détails Lead (LMLead.js) */}
      {selected && (
        <div className="overlayAC">
          <div className="inoverlayLead" ref={detailRef}>
            <button
              className="close-button"
              onClick={(e) => {
                e.stopPropagation();
                setSelected(null);
              }}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                zIndex: 10000,
              }}
            >
              <button
                aria-label="Fermer"
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </button>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                padding: "0 5%",
                boxSizing: "border-box",
              }}
            >
              {/* Header */}
              <div className="flex justify-between mb-6 items-center">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center">
                    <CreditCard className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="text-1xl font-bold text-slate-900 m-0">
                      {selected.nom || "—"}
                    </div>
                    <div className="text-1xl font-bold text-slate-400 m-0 font-mono">
                      {displayRum(selected.rum) || "Mandat SEPA"}
                    </div>
                    {statusBadge(selected.signerStatus || selected.status)}
                  </div>
                </div>
                <div className="flex items-center gap-3 mr-10">
                  {/* Badge du cabinet créancier du mandat */}
                  {selected.cabinet && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#9B1B5D]/10 text-[#9B1B5D] border border-[#9B1B5D]/20">
                      <Building2 className="w-3.5 h-3.5" />
                      {selected.cabinet}
                    </span>
                  )}
                  {/* Téléchargement du document mandat, signé ou non */}
                  {hasMandatDocument(selected) && (
                    <button
                      onClick={(e) => handleTelechargerMandat(selected, e)}
                      disabled={downloadingDoc.has(selected.id)}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        isMandatSigne(selected)
                          ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                          : "bg-slate-500 hover:bg-slate-600 text-white"
                      }`}
                    >
                      {downloadingDoc.has(selected.id) ? (
                        <span className="animate-spin inline-block w-3 h-3 border border-white border-t-transparent rounded-full" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      {isMandatSigne(selected)
                        ? "Télécharger le mandat signé"
                        : "Télécharger le mandat non signé"}
                    </button>
                  )}
                </div>
              </div>

              {/* Identité + Coordonnées bancaires */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#9B1B5D]" />
                    Identité
                  </h3>
                  <div className="space-y-2">
                    <DetailRow label="Nom" value={selected.nom} />
                    <DetailRow label="Email" value={selected.email} />
                    <DetailRow label="Cabinet" value={selected.cabinet} />
                    {selected.lienEnvoye && (
                      <DetailRow
                        label="Lien de mandat envoyé le"
                        value={formatDate(selected.lienEnvoyeLe)}
                      />
                    )}
                    {(selected.rue ||
                      selected.adresse_complement ||
                      selected.code_postal ||
                      selected.pays) && (
                      <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                        <p className="text-xs font-semibold text-slate-700 mb-1">
                          Adresse
                        </p>
                        <DetailRow label="Rue" value={selected.rue} />
                        <DetailRow
                          label="Complément d'adresse"
                          value={selected.adresse_complement}
                        />
                        <DetailRow
                          label="Code postal"
                          value={selected.code_postal}
                        />
                        <DetailRow label="Pays" value={selected.pays} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#9B1B5D]" />
                    Coordonnées bancaires
                  </h3>
                  <div className="space-y-2">
                    <DetailRow
                      label="IBAN"
                      value={selected.rib ? formatIban(selected.rib) : null}
                    />
                    <DetailRow label="BIC" value={selected.bic} />
                    <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
                      <p className="text-xs font-semibold text-slate-700 mb-1">
                        Signature
                      </p>
                      <DetailRow
                        label="Date de signature"
                        value={formatDate(selected.dateSignature)}
                      />
                      <DetailRow label="Lieu" value={selected.lieu} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Client lié + DocuSign */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
                {selected.client && (
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                      Client lié
                    </h3>
                    <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate m-0">
                          {selected.client.nom}
                        </p>
                        {selected.client.email && (
                          <p className="text-xs text-slate-500 truncate m-0">
                            {selected.client.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selected.docusignEnvelopeId && (
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#9B1B5D]" />
                      DocuSign
                    </h3>
                    <div className="space-y-2">
                      <DetailRow
                        label="Envelope ID"
                        value={selected.docusignEnvelopeId}
                      />
                      <DetailRow
                        label="Statut envelope"
                        value={selected.status}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Documents liés */}
              <div className="bg-white border border-slate-200 rounded-lg p-4 mt-5 mb-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#9B1B5D]" />
                  Documents liés
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-10 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Document RIB :</span>
                    {selected.document ? (
                      <a
                        href={selected.document}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#9B1B5D] font-medium hover:underline"
                      >
                        Voir le document
                      </a>
                    ) : (
                      <span className="text-slate-400">Aucun fichier</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">
                      Mandat {isMandatSigne(selected) ? "signé" : "non signé"}{" "}
                      :
                    </span>
                    {hasMandatDocument(selected) ? (
                      <button
                        onClick={(e) => handleTelechargerMandat(selected, e)}
                        disabled={downloadingDoc.has(selected.id)}
                        className="text-[#9B1B5D] font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Télécharger le document
                      </button>
                    ) : (
                      <span className="text-slate-400">Aucun fichier</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation générique */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => {
            setShowConfirmModal(null);
            setSignedPdfFile(null);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`px-6 py-4 bg-gradient-to-r ${
                showConfirmModal.type === "supprimer"
                  ? "from-red-600 to-red-500"
                  : showConfirmModal.type === "revoir"
                  ? "from-amber-500 to-amber-400"
                  : "from-emerald-600 to-emerald-500"
              }`}
            >
              <p className="text-white/70 text-[10px] uppercase tracking-widest mb-0.5">
                Confirmation
              </p>
              <h3 className="text-white font-semibold text-sm">
                {showConfirmModal.type === "supprimer"
                  ? "Supprimer"
                  : showConfirmModal.type === "revoir"
                  ? "Remettre à revoir"
                  : "Clôturer"}{" "}
                le mandat
              </h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm font-medium text-gray-800 mb-1">
                {showConfirmModal.mandat.nom ||
                  displayRum(showConfirmModal.mandat.rum) ||
                  "Ce mandat"}
              </p>
              <p
                className={`text-xs text-gray-500 ${
                  showConfirmModal.type === "cloture" ? "mb-3" : "mb-5"
                }`}
              >
                {showConfirmModal.type === "supprimer"
                  ? "Cette action est irréversible. Le mandat sera définitivement supprimé."
                  : showConfirmModal.type === "revoir"
                  ? 'Le statut repassera à "À revoir". Les actions Clôturer et Annuler seront de nouveau disponibles.'
                  : "Le mandat sera marqué comme clôturé (Signé)."}
              </p>

              {showConfirmModal.type === "cloture" && (
                <div className="mb-4">
                  <label className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1">
                    <span>
                      Mandat signé{" "}
                      <span className="font-normal text-slate-400">
                        (optionnel)
                      </span>
                    </span>
                  </label>
                  <div
                    onClick={() => signedPdfInputRef.current?.click()}
                    className={`relative flex items-center gap-3 h-11 px-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                      signedPdfFile
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-slate-200 hover:border-[#9B1B5D]/40 hover:bg-slate-50"
                    }`}
                  >
                    <Upload
                      className={`w-4 h-4 shrink-0 ${
                        signedPdfFile ? "text-emerald-600" : "text-slate-400"
                      }`}
                    />
                    <span
                      className={`text-xs truncate flex-1 ${
                        signedPdfFile
                          ? "text-emerald-700 font-medium"
                          : "text-slate-400"
                      }`}
                    >
                      {signedPdfFile
                        ? signedPdfFile.name
                        : "Cliquez pour importer le PDF signé"}
                    </span>
                    {signedPdfFile && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSignedPdfFile(null);
                          if (signedPdfInputRef.current)
                            signedPdfInputRef.current.value = "";
                        }}
                        className="ml-auto text-slate-400 hover:text-slate-600 shrink-0 text-lg leading-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <input
                    ref={signedPdfInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0] || null;
                      setSignedPdfFile(file);
                    }}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowConfirmModal(null);
                    setSignedPdfFile(null);
                  }}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    const { type, mandat } = showConfirmModal;
                    const fileToUpload = signedPdfFile;
                    setShowConfirmModal(null);
                    setSignedPdfFile(null);
                    if (type === "cloture")
                      handleCloture(mandat, fileToUpload);
                    else if (type === "revoir") handleRevoir(mandat);
                    else if (type === "supprimer") handleSupprimer(mandat);
                  }}
                  className={`flex-1 px-4 py-2 rounded-xl text-white text-xs font-medium transition-colors ${
                    showConfirmModal.type === "supprimer"
                      ? "bg-red-500 hover:bg-red-600"
                      : showConfirmModal.type === "revoir"
                      ? "bg-amber-500 hover:bg-amber-600"
                      : "bg-emerald-500 hover:bg-emerald-600"
                  }`}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal annulation */}
      {showAnnulModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => {
            setShowAnnulModal(null);
            setMotifInput("");
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-600 to-red-500 px-6 py-4">
              <p className="text-white/70 text-[10px] uppercase tracking-widest mb-0.5">
                Annulation
              </p>
              <h3 className="text-white font-semibold text-sm">
                {showAnnulModal.nom || displayRum(showAnnulModal.rum) || "Mandat"}
              </h3>
            </div>
            <div className="px-6 py-5">
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                Motif de l'annulation <span className="text-red-500">*</span>
              </label>
              <textarea
                autoFocus
                rows={4}
                value={motifInput}
                onChange={(e) => setMotifInput(e.target.value)}
                placeholder="Saisissez le motif de l'annulation…"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-800 resize-none focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setShowAnnulModal(null);
                    setMotifInput("");
                  }}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={confirmAnnulation}
                  disabled={!motifInput.trim()}
                  className="flex-1 px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 disabled:bg-red-200 text-white text-xs font-medium transition-colors disabled:cursor-not-allowed"
                >
                  Confirmer l'annulation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
